import { z } from "zod";
import type { Operation } from "../types";

/**
 * Pulse — the universal Health-Wall wire contract (docs/lifecycle-spine/PRD.md
 * §8, §10). `GET /api/pulse/[scope]` returns one `AggGroup[]` wall (one cluster
 * per function band, one bar per touchpoint, aggregated across the scope's
 * subjects) plus the mode rollup. The `<Pulse>` block (`blocks/pulse/pulse-wall`)
 * is the SWR consumer; the route is `createRoute(pulse.aggregate, …)` over the
 * P0 engine (`healthAggregate`/`overallOf` from `@excellent/core`).
 *
 * `scope` is a path param: a mode slug (`"customers"`), `"workspace"` (the L0
 * cross-mode wall), `"<mode>:<area>"` (a domain-scoped wall, e.g.
 * `"customers:sales"`), `"<mode>:lane:<slug>"` (Pulse Graph Stage 1 — the wall
 * narrowed to ONE Lane's bars, the group whose key === slug), or
 * `"<mode>:o:<uid>"` (the single-entity lens). The AggGroup/AggTouch shapes mirror the core TS types
 * (`AggGroup`/`AggTouch` in `packages/core/src/pulse/types.ts`) — kept as real
 * zod objects so the envelope validates, with `.passthrough()` on the nested
 * shapes so an additive engine field never trips the client parser.
 */

export const PULSE_BANDS = ["ok", "watch", "risk"] as const;
export type PulseBand = (typeof PULSE_BANDS)[number];

export const PULSE_WEIGHTS = ["count", "value"] as const;
export type PulseWeight = (typeof PULSE_WEIGHTS)[number];

/** Calibration assessment carried on each bar (§6) — the target-tuning verdict. */
export const pulseCalibration = z
  .object({
    target: z.number(),
    attainment: z.number(),
    position: z.number(),
    verdict: z.enum(["no-signal", "too-loose", "too-strict", "well-set"]),
    sample: z.number().int(),
    suggestion: z
      .object({
        target: z.number(),
        why: z.string(),
        confidence: z.enum(["low", "med", "high"]),
      })
      .optional(),
  })
  .passthrough();
export type PulseCalibrationDTO = z.infer<typeof pulseCalibration>;

/** One subject's contribution to a bar (worst-first), used by the L1 breakdown. */
export const pulsePerEntity = z
  .object({
    entity: z
      .object({
        id: z.number().int(),
        name: z.string(),
        uid: z.string().optional(),
      })
      .passthrough(),
    health: z.number(),
  })
  .passthrough();
export type PulsePerEntityDTO = z.infer<typeof pulsePerEntity>;

/**
 * One drill-resolved subject row for the L1 BREAKDOWN (`pulse.breakdown`). Same
 * worst-first health as `pulsePerEntity`, plus the server-computed L2 drill
 * `href` (`/o/<uid>`, the entity → Object-page target per §8 `drillTargetOf`).
 * The block stays IA-agnostic: it never derives a route, it follows `href`.
 */
export const pulseDrillEntity = z
  .object({
    entity: z
      .object({
        id: z.number().int(),
        name: z.string(),
        uid: z.string().optional(),
      })
      .passthrough(),
    health: z.number(),
    /** The L2 drill target, server-computed (`/o/<uid>`); null when no uid. */
    href: z.string().nullable(),
  })
  .passthrough();
export type PulseDrillEntityDTO = z.infer<typeof pulseDrillEntity>;

/**
 * One PRESCRIPTION attached to a below-target bar (§9.2). Sourced from the
 * EXISTING `sectionAgentSuggestions` engine (Pulse forks no recommender,
 * invariant §9.9), bound to the bar by its `group.domain.area` → department.
 * `kind` drives the deploy dispatch: `flow` activates a draft loop, `agent`
 * deploys a worker, `play`/`tool` link out. `ref` carries the activation
 * target (e.g. the loop slug for a `flow`); `cta` is the deploy affordance —
 * `href` set ⇒ a link-out (no write), absent ⇒ a one-click POST /pulse/prescribe.
 */
export const pulseSuggestion = z
  .object({
    id: z.string(),
    kind: z.enum(["flow", "agent", "play", "job", "tool"]),
    title: z.string(),
    rationale: z.string().optional(),
    evidence: z.string().optional(),
    /** The activation target — the loop slug (`flow`), role (`agent`), … */
    ref: z.string().optional(),
    cta: z
      .object({ label: z.string(), href: z.string().optional() })
      .optional(),
  })
  .passthrough();
export type PulseSuggestionDTO = z.infer<typeof pulseSuggestion>;

/** One bar — a touchpoint aggregated across the scope (`health:null` = ghost). */
export const pulseTouch = z
  .object({
    id: z.string(),
    label: z.string(),
    kind: z.enum([
      "automation",
      "stage",
      "channel",
      "signal",
      "metric",
      "queue",
      "task",
      "integration",
    ]),
    readiness: z.enum(["live", "derived", "planned"]),
    metric: z.string().optional(),
    health: z.number().nullable(),
    target: z.number(),
    activeCount: z.number().int(),
    atRisk: z.number().int(),
    perEntity: z.array(pulsePerEntity),
    calibration: pulseCalibration.optional(),
    /** Range-over-range trend (§6.2 — the calibration arrow). Omitted when the
     *  prior window has no comparable value (a ghost on either side). */
    trend: z.enum(["up", "flat", "down"]).optional(),
    /** Below-target bars only (§9.2): the top 1–2 prescribed fixes, ranked. */
    suggestions: z.array(pulseSuggestion).optional(),
    /** SUGGESTED (draft) bar marker (§9.3, the third bar state). A draft/paused
     *  flow that WOULD instrument this band, appended server-side as a
     *  `+ activate` bar (`health:null`, distinct from a planned ghost). The
     *  `loopSlug` is the activation target (POST /pulse/prescribe {kind:"flow"}). */
    suggested: z
      .object({
        loopSlug: z.string().optional(),
        title: z.string(),
        evidence: z.string().optional(),
      })
      .optional(),
    /** Optional real-navigation drill target (§2, §8). When set, the Workspace
     *  L0 mode-bar links out to `href` (`/<mode>`) instead of the in-wall drill;
     *  server-computed so the block stays IA-agnostic. */
    href: z.string().optional(),
  })
  .passthrough();
export type PulseTouchDTO = z.infer<typeof pulseTouch>;

/** One function band — a cluster of bars + the band rollup. */
export const pulseGroup = z
  .object({
    key: z.string(),
    label: z.string(),
    icon: z.string(),
    domain: z
      .object({ mode: z.string(), area: z.string() })
      .passthrough(),
    touches: z.array(pulseTouch),
    health: z.number().nullable(),
    /** Group-header real-navigation target (the nested rollup board, §3.4):
     *  a stream header dives into its mode home. Server-computed, flag-aware. */
    href: z.string().optional(),
    /** Group-level live work (the nested rollup board): a mode-wall section's
     *  firing JTBD jobs. The per-touchpoint fixes stay on each touch. */
    work: z.array(pulseSuggestion).optional(),
  })
  .passthrough();
export type PulseGroupDTO = z.infer<typeof pulseGroup>;

const aggregateParams = z.object({ scope: z.string().min(1) });
export type PulseAggregateParams = z.infer<typeof aggregateParams>;

const aggregateQuery = z.object({
  /** Aggregation weighting override (default: the provider's). */
  weight: z.enum(PULSE_WEIGHTS).optional(),
  /** Trend window in days (default 30). Coerced — it arrives as a string. */
  days: z.coerce.number().int().positive().max(365).optional(),
  /**
   * Rollup altitude (docs/lifecycle-spine/SECTION-PULSE-PRD.md §2). `"sections"`
   * collapses a mode wall to ONE bar per section (the section-aggregate home);
   * absent ⇒ the default per-touchpoint wall. The `project:<slug>` scope is
   * always a section rollup (a project has no touchpoint provider), so this
   * param is the mode-scope selector.
   */
  rollup: z.enum(["sections"]).optional(),
  /**
   * Project tier (Pulse Graph Stage 1 fix): when the wall is rendered at
   * `/projects/<slug>/<mode>`, the band-dive href must stay INSIDE the project
   * (`/projects/<slug>/<mode>?area=<key>`) rather than leaving to the workspace
   * tier. The route only sees the bare mode token in `scope`, so the project
   * scope rides here and the band-href builder uses it. Absent ⇒ workspace tier.
   */
  projectSlug: z.string().min(1).optional(),
});
export type PulseAggregateQuery = z.infer<typeof aggregateQuery>;

const aggregateResponse = z.object({
  groups: z.array(pulseGroup),
  /** Mode rollup 0..100 (`overallOf` over the wall). */
  overall: z.number(),
  /** The resolved scope label echoed back (e.g. "customers", "customers:sales"). */
  scope: z.string(),
  /** The subject plural for the status strip (e.g. "Customers", "Subjects"). */
  plural: z.string(),
  /**
   * The bar UNIT for the status-strip wording (SECTION-PULSE-PRD §4). Set to
   * `"section"` on a section rollup so the strip reads "N sections" /
   * "bottleneck sections" instead of "N touchpoints"; absent ⇒ "touchpoints".
   */
  unit: z.string().optional(),
});
export type PulseAggregateResponse = z.infer<typeof aggregateResponse>;

const aggregateOp: Operation<
  PulseAggregateParams,
  PulseAggregateQuery,
  void,
  PulseAggregateResponse
> = {
  method: "GET",
  path: (p) => `/api/pulse/${encodeURIComponent(p.scope)}`,
  params: aggregateParams,
  query: aggregateQuery,
  response: aggregateResponse,
};

// ── pulse.breakdown — the L1 BREAKDOWN of one bottleneck (§8) ──────────────────
//
// `GET /api/pulse/[scope]/[touch]` drills a bottleneck bar to its detail: the
// PORTFOLIO per-entity bars (worst-first, one bar per subject — "who is dragging
// it down") or, for a SINGLE-SUBJECT scope, the `components` behind the number.
// Drill targets are resolved SERVER-SIDE (the block stays IA-agnostic, §8):
// `perEntity[i].href = /o/<uid>` and `group.domainHref` = the band's domain home.

const breakdownParams = z.object({
  scope: z.string().min(1),
  /** The touchpoint id (`?t=` deep-link), unique within its group. */
  touch: z.string().min(1),
});
export type PulseBreakdownParams = z.infer<typeof breakdownParams>;

const breakdownQuery = z.object({
  weight: z.enum(PULSE_WEIGHTS).optional(),
  days: z.coerce.number().int().positive().max(365).optional(),
});
export type PulseBreakdownQuery = z.infer<typeof breakdownQuery>;

/** The group identity + its domain-home drill href (group → domain, §8). */
export const pulseBreakdownGroup = z
  .object({
    key: z.string(),
    label: z.string(),
    icon: z.string(),
    domain: z.object({ mode: z.string(), area: z.string() }).passthrough(),
    /** Server-computed `domainHomeRoute({mode, slug: area})` (`drillTargetOf`). */
    domainHref: z.string(),
  })
  .passthrough();
export type PulseBreakdownGroupDTO = z.infer<typeof pulseBreakdownGroup>;

const breakdownResponse = z.object({
  group: pulseBreakdownGroup,
  /** The drilled bar (the same AggTouch shape the L0 wall renders). */
  touch: pulseTouch,
  /** PORTFOLIO scope: one drill-resolved row per subject (worst-first). */
  perEntity: z.array(pulseDrillEntity),
  /** SINGLE-SUBJECT scope: the component breakdown (rows/checks), if declared. */
  components: z.array(z.object({ label: z.string(), health: z.number() })).optional(),
  /** The resolved scope label echoed back. */
  scope: z.string(),
});
export type PulseBreakdownResponse = z.infer<typeof breakdownResponse>;

const breakdownOp: Operation<
  PulseBreakdownParams,
  PulseBreakdownQuery,
  void,
  PulseBreakdownResponse
> = {
  method: "GET",
  path: (p) =>
    `/api/pulse/${encodeURIComponent(p.scope)}/${encodeURIComponent(p.touch)}`,
  params: breakdownParams,
  query: breakdownQuery,
  response: breakdownResponse,
};

// ── pulse.setTarget — tune a target / weight (§6.1) ───────────────────────────
//
// `POST /api/pulse/targets` writes a per-workspace `settings` row (additive JSON,
// NO migration). Two write modes share the endpoint:
//   · per-bar target  → key `pulse.target.<mode>.<groupKey>.<touchId>`,
//                        value `{ target, rationale?, weight? }`
//   · per-mode weight  → key `pulse.weight.<mode>`, value `"count" | "value"`
// At least one of `target` / `weight` (with `groupKey`+`touchId` for a target,
// or `weight` alone for the mode-level toggle) must be present.

const setTargetBody = z
  .object({
    mode: z.string().min(1),
    /** Required for a per-bar target write; omitted for a mode-level weight. */
    groupKey: z.string().min(1).optional(),
    touchId: z.string().min(1).optional(),
    /** The tuned benchmark 0..100 (cleared when omitted on a target write). */
    target: z.number().min(0).max(100).optional(),
    /** Per-mode aggregation weight (the value/count toggle). */
    weight: z.enum(PULSE_WEIGHTS).optional(),
    /** Optional one-line "why" persisted alongside the target. */
    rationale: z.string().max(500).optional(),
  })
  .refine(
    (b) =>
      b.weight != null ||
      (b.groupKey != null && b.touchId != null && (b.target != null || b.rationale != null)),
    {
      message:
        "provide a per-mode `weight`, or `groupKey`+`touchId` with a `target`/`rationale`",
    },
  );
export type PulseSetTargetBody = z.infer<typeof setTargetBody>;

const setTargetResponse = z.object({ ok: z.boolean() });
export type PulseSetTargetResponse = z.infer<typeof setTargetResponse>;

const setTargetOp: Operation<void, void, PulseSetTargetBody, PulseSetTargetResponse> = {
  method: "POST",
  path: () => "/api/pulse/targets",
  body: setTargetBody,
  response: setTargetResponse,
};

// ── pulse.prescribe — one-click deploy a bar's prescribed fix (§9.2) ──────────
//
// `POST /api/pulse/prescribe` dispatches by suggestion `kind` to the EXISTING
// server activation helpers (Pulse forks no recommender NOR activation path):
//   · flow  → activate a draft loop (draft→active, `setLoopStatusAction`) — `ref`
//             is the loop slug.
//   · agent → deploy a worker via `goal_create({title, role})` — `role` is the
//             worker role (defaults to a generally-available role).
//   · play  → inject a `kind:"fix"` Play action keyed `pulse:<mode>:<group>:<touch>`,
//             deduped on (workspace_id, action_key) — so the bottleneck ranks in
//             the Play queue with its *why* = the red bar.
// The bar address (`mode`/`groupKey`/`touchId`/`sectionSlug`) anchors the Play
// dedupe key + the source_ref; `title` carries the suggestion headline.

const prescribeBody = z
  .object({
    kind: z.enum(["flow", "agent", "play"]),
    /** The bar this prescription fixes — anchors the Play dedupe key + ref. */
    mode: z.string().min(1),
    groupKey: z.string().min(1),
    touchId: z.string().min(1),
    /** The department section slug the suggestion was drawn from. */
    sectionSlug: z.string().min(1).optional(),
    /** The suggestion headline (Play card title / goal title). */
    title: z.string().min(1),
    /** flow → the loop slug; agent → the worker role. */
    ref: z.string().optional(),
    /** Short evidence line, persisted as the Play card body. */
    evidence: z.string().optional(),
  })
  .refine((b) => b.kind !== "flow" || (b.ref != null && b.ref.length > 0), {
    message: "a `flow` prescription needs the loop slug in `ref`",
  });
export type PulsePrescribeBody = z.infer<typeof prescribeBody>;

const prescribeResponse = z.object({
  ok: z.boolean(),
  /** What happened: "activated" | "goal-created" | "queued" | the failure. */
  outcome: z.string(),
  /** A surface to follow after deploy (the activated loop / new goal / Play). */
  href: z.string().optional(),
});
export type PulsePrescribeResponse = z.infer<typeof prescribeResponse>;

const prescribeOp: Operation<void, void, PulsePrescribeBody, PulsePrescribeResponse> = {
  method: "POST",
  path: () => "/api/pulse/prescribe",
  body: prescribeBody,
  response: prescribeResponse,
};

// ── pulse.prescribePreview — describe what activating a prescription will do ───
//
// `GET /api/pulse/prescribe-preview?kind=<>&ref=<>` — a READ that powers the
// "Turn on" confirmation modal. One-click activating an autonomous flow ("runs
// end-to-end on your schedule") is consequential, so the modal shows what it
// actually does — its step chain, what it replaces, how it's governed (the gate)
// — before it fires. No write; the confirm button still POSTs `prescribe`.

const prescribePreviewQuery = z.object({
  kind: z.enum(["flow", "agent", "play"]),
  /** flow → the loop slug; agent → the worker role. */
  ref: z.string().optional(),
});
export type PulsePrescribePreviewQuery = z.infer<typeof prescribePreviewQuery>;

export const prescribePreviewStep = z.object({
  /** Humanized step label ("Draft customer email"). */
  label: z.string(),
  /** The worker role that runs the step, if any. */
  role: z.string().nullable(),
});
export type PrescribePreviewStepDTO = z.infer<typeof prescribePreviewStep>;

const prescribePreviewResponse = z.object({
  kind: z.enum(["flow", "agent", "play"]),
  /** Canonical title (the loop title for a flow); the client falls back to the
   *  row title when empty. */
  title: z.string(),
  /** One-line plain-English summary (stripped of catalog marketing), or null. */
  summary: z.string().nullable(),
  /** The ordered steps this flow runs — "what it actually does". */
  steps: z.array(prescribePreviewStep),
  stepCount: z.number().int(),
  /** What turning it on automates away (the catalog "Replaces:" line), or null. */
  replaces: z.string().nullable(),
  /** Plain-English line: what activation does + how it's governed. */
  effect: z.string(),
  /** The worker role an `agent` deploy creates (null for flow/play). */
  role: z.string().nullable(),
  /** Full-detail surface (the Workflow Studio page / autopilot settings). */
  detailHref: z.string().nullable(),
  /** False ⇒ the ref didn't resolve (a stale draft) — the modal warns + blocks. */
  found: z.boolean(),
});
export type PulsePrescribePreviewResponse = z.infer<typeof prescribePreviewResponse>;

const prescribePreviewOp: Operation<
  void,
  PulsePrescribePreviewQuery,
  void,
  PulsePrescribePreviewResponse
> = {
  method: "GET",
  path: () => "/api/pulse/prescribe-preview",
  query: prescribePreviewQuery,
  response: prescribePreviewResponse,
};

// ── pulse.synthesisRead / synthesisBatch — the LLM-generated editorial READ ────
//
// The deterministic `buildPulseSynthesis`/`buildWorkspaceSynthesis`
// (pulse-synthesis-build.ts) render instantly and are the guaranteed floor; these
// endpoints LAYER a Claude-generated read on top via the local-CLI seam
// (`spawnClaudeJson`, the same path advisor/market/optimization use). The client
// POSTs the slim, ALTITUDE-AGNOSTIC brief it already built from the aggregate it
// fetched; the server generates (cached by brief-hash, single-flight, OPUS, a
// global concurrency cap) and returns the read — or `read:null` on any failure,
// so the client keeps its formula read. The brief-hash rides in `?h=` so SWR
// re-fetches when the pulse materially changes (the cache key is URL-only); the
// SAME hash is the server cache key, so a changed pulse auto-regenerates and an
// unchanged one is free. The home pre-warms every mode read via `synthesisBatch`
// so navigating to a mode is an instant cache hit. See
// docs/design/SYNTHESIS-READ-GENERATOR.md.

const synthesisBriefTouch = z
  .object({
    id: z.string(),
    label: z.string(),
    /** null = NOT INSTRUMENTED (no data); a number = measured. */
    health: z.number().nullable(),
    target: z.number(),
    atRisk: z.number().int(),
    trend: z.enum(["up", "flat", "down"]).nullable().optional(),
    /** The top prescription title (the "Fix:" hint), when one is bound. */
    fix: z.string().nullable().optional(),
  })
  .passthrough();

const synthesisBriefGroup = z
  .object({
    key: z.string(),
    label: z.string(),
    health: z.number().nullable(),
    /** True when every signal in this component is uninstrumented. */
    allUninstrumented: z.boolean(),
    touches: z.array(synthesisBriefTouch),
  })
  .passthrough();

/**
 * The altitude-agnostic read brief. `groups` are the COMPONENTS of `subject`
 * (workspace → areas; a function → its sections; a band → its one section), each
 * with its underlying SIGNALS (`touches`). `focus` (a group key) narrows the read
 * to one component. The same shape feeds every page's read.
 */
export const synthesisBrief = z
  .object({
    subject: z.string(),
    /** The pulse scope this read is for (e.g. "workspace", "work", "work:lane:goals"). */
    scope: z.string(),
    overall: z.number().nullable(),
    focus: z.string().nullable().optional(),
    groups: z.array(synthesisBriefGroup),
  })
  .passthrough();
export type PulseSynthesisBrief = z.infer<typeof synthesisBrief>;

const synthesisReadQuery = z.object({
  /** Stable hash of the brief — keys SWR (URL-only cache key) + the server cache. */
  h: z.string().min(1),
});

const synthesisReadResult = z
  .object({
    /** The generated read, or null when none is available (→ use the formula). */
    read: z.string().nullable(),
    /** The exact brief fields the read cites (honesty trace; may be empty). */
    grounding: z.array(z.string()),
  })
  .passthrough();
export type PulseSynthesisReadResponse = z.infer<typeof synthesisReadResult>;

const synthesisReadOp: Operation<
  void,
  z.infer<typeof synthesisReadQuery>,
  PulseSynthesisBrief,
  PulseSynthesisReadResponse
> = {
  method: "POST",
  path: () => "/api/pulse/synthesis",
  query: synthesisReadQuery,
  body: synthesisBrief,
  response: synthesisReadResult,
};

// ── pulse.synthesisBatch — pre-warm many reads in one bounded-parallel pass ────
const synthesisBatchBody = z.object({
  items: z.array(z.object({ h: z.string().min(1), brief: synthesisBrief })).max(16),
});
export type PulseSynthesisBatchBody = z.infer<typeof synthesisBatchBody>;

const synthesisBatchResponse = z.object({
  /** Per-hash results. Misses still generating return read:null now (fire-and-forget);
   *  the per-page `synthesisRead` call picks them up once warm. */
  results: z.array(z.object({ h: z.string(), read: z.string().nullable(), grounding: z.array(z.string()) })),
});
export type PulseSynthesisBatchResponse = z.infer<typeof synthesisBatchResponse>;

const synthesisBatchOp: Operation<
  void,
  void,
  PulseSynthesisBatchBody,
  PulseSynthesisBatchResponse
> = {
  method: "POST",
  path: () => "/api/pulse/synthesis/batch",
  body: synthesisBatchBody,
  response: synthesisBatchResponse,
};

/* ──────────────────────────────────────────────────────────────────────────────
 * RO-2-11 — `excellent/src/pulse/lane-actions.ts` (Pulse Graph Stage 2,
 * docs/lifecycle-spine/PULSE-GRAPH-PRD.md §3): bind / unbind an automation
 * (workflow, i.e. an `excellent_loops` row) to a Lane by setting the additive v77
 * `lane_uid` column.
 *
 * THE UNBLOCK: these two are the last thing standing between RO-3-00c — the
 * critical path of the entire L3 lane — and `_modes/lane-page.tsx` +
 * `_modes/lane-automations.tsx`.
 *
 * Both are body-only POSTs with no `:param` segment, so neither touches Hono's
 * registration-order shadowing hazard (#44370), and the pair is symmetric rather
 * than POST/DELETE — a DELETE carrying the loop slug in the path would collide
 * with `/api/pulse/:scope`, and #44276's lesson is that method choice here is a
 * correctness question, not a style one.
 *
 * ACTIVATE/PAUSE IS NOT HERE, deliberately: the source file's own header says
 * that is `setLoopStatusAction` (src/optimization/actions.ts, RO-2-09e/#44418's
 * bucket) and is "not re-implemented here". This port keeps that boundary.
 *
 * The source actions take a trailing `revalidate?: string` path argument, which
 * an endpoint cannot honor; it is dropped and recorded at the mount for L5.
 * ────────────────────────────────────────────────────────────────────────────── */

export type PulseLaneAutomationResult = { ok: boolean; error?: string };

const laneAutomationResponse = z.object({
  ok: z.boolean(),
  /** `missing-args` | `loop-not-found` | `loop-retired`, verbatim from the source. */
  error: z.string().optional(),
});

const laneAttachBody = z.object({ loopSlug: z.string(), laneUid: z.string() });
export type PulseLaneAttachBody = z.infer<typeof laneAttachBody>;

const laneAttachOp: Operation<void, void, PulseLaneAttachBody, PulseLaneAutomationResult> = {
  method: "POST",
  path: () => "/api/pulse/lane-automations/attach",
  body: laneAttachBody,
  response: laneAutomationResponse,
};

const laneDetachBody = z.object({ loopSlug: z.string() });
export type PulseLaneDetachBody = z.infer<typeof laneDetachBody>;

const laneDetachOp: Operation<void, void, PulseLaneDetachBody, PulseLaneAutomationResult> = {
  method: "POST",
  path: () => "/api/pulse/lane-automations/detach",
  body: laneDetachBody,
  response: laneAutomationResponse,
};

/* ──────────────────────────────────────────────────────────────────────────────
 * RO-2-11b / #44481 — `excellent/src/pulse/graph/actions.ts` (Pulse Graph Stage 3,
 * PULSE-GRAPH-PRD.md §6/§8): regenerate the customer-lifecycle graph, accept a
 * proposed Lane, dismiss a proposed Lane.
 *
 * Unlike RO-2-11's lane-actions (which write `excellent_loops.lane_uid` and
 * therefore carry `loop:mutate`), these three DO touch `excellent_pulse_lanes`
 * via `setLaneStatus` / `applyGeneratedGraph` — so the MCP twins
 * `pulse_graph_generate` / `pulse_lane_update` (`pulse:mutate`) are correct.
 *
 * The source actions take a trailing `revalidate?: string` / hard-coded
 * `revalidatePath("/customers")`; both dropped and recorded at the mount for L5.
 * ────────────────────────────────────────────────────────────────────────────── */

const graphRegenerateBody = z.object({ refine: z.boolean().optional() });
export type PulseGraphRegenerateBody = z.infer<typeof graphRegenerateBody>;

const graphRegenerateResult = z.object({
  ok: z.boolean(),
  firstRun: z.boolean(),
  archetype: z.string(),
  applied: z.number(),
  proposed: z.number(),
  skippedUser: z.number(),
  edges: z.number(),
});
export type PulseGraphRegenerateResult = z.infer<typeof graphRegenerateResult>;

const graphRegenerateOp: Operation<
  void,
  void,
  PulseGraphRegenerateBody,
  PulseGraphRegenerateResult
> = {
  method: "POST",
  path: () => "/api/pulse/graph/regenerate",
  body: graphRegenerateBody,
  response: graphRegenerateResult,
};

const proposedLaneBody = z.object({
  uid: z.string().min(1),
  /** Next-page path the source accepted for revalidatePath — ignored on the host. */
  revalidate: z.string().optional(),
});
export type PulseProposedLaneBody = z.infer<typeof proposedLaneBody>;

const proposedLaneResult = z.object({ ok: z.boolean() });
export type PulseProposedLaneResult = z.infer<typeof proposedLaneResult>;

const graphAcceptLaneOp: Operation<void, void, PulseProposedLaneBody, PulseProposedLaneResult> = {
  method: "POST",
  path: () => "/api/pulse/graph/lanes/accept",
  body: proposedLaneBody,
  response: proposedLaneResult,
};

const graphDismissLaneOp: Operation<void, void, PulseProposedLaneBody, PulseProposedLaneResult> = {
  method: "POST",
  path: () => "/api/pulse/graph/lanes/dismiss",
  body: proposedLaneBody,
  response: proposedLaneResult,
};

export const pulse = {
  aggregate: aggregateOp,
  breakdown: breakdownOp,
  setTarget: setTargetOp,
  prescribe: prescribeOp,
  prescribePreview: prescribePreviewOp,
  synthesisRead: synthesisReadOp,
  synthesisBatch: synthesisBatchOp,
  // RO-2-11 — src/pulse/lane-actions.ts, the RO-3-00c unblock.
  laneAttach: laneAttachOp,
  laneDetach: laneDetachOp,
  // RO-2-11b / #44481 — src/pulse/graph/actions.ts.
  graphRegenerate: graphRegenerateOp,
  graphAcceptLane: graphAcceptLaneOp,
  graphDismissLane: graphDismissLaneOp,
};
